import {
  $convertFromMarkdownString,
  ELEMENT_TRANSFORMERS,
  type ElementTransformer,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  type Transformer,
  TRANSFORMERS as LEXICAL_TRANSFORMERS,
} from '@lexical/markdown';
import {TableCellNode, TableNode, TableRowNode} from '@lexical/table';
import {type LexicalNode, ParagraphNode} from 'lexical';
import {$createEnhancedHeadingNode} from 'plugins/enhancedHeadings/EnhancedHeadingNode';

import {$isImageNode} from './nodes/ImageNode';

// Helper to export a node to markdown using the transformers
export function exportNodeToMarkdown(node: any, transformers: Transformer[]): string {
  // if (node.getType() === 'redaction-mark') {
  //   return '[REDACTED]';
  // }

  if (node.getType() === 'paragraph') {
    const paragraphNode = node as ParagraphNode;

    const childrenMarkdown = paragraphNode
      .getChildren()
      .map((child) =>
        exportNodeToMarkdown(child, [
          // LIST_TRANSFORMER,
          ...ELEMENT_TRANSFORMERS,
          ...TEXT_FORMAT_TRANSFORMERS,
          ...TEXT_MATCH_TRANSFORMERS,
        ])
      )
      .join('');

    // Add newline after paragraph content
    return childrenMarkdown + '\n';
  }

  if ($isImageNode && $isImageNode(node)) {
    return `:${node.getAltText()}:`;
  }

  // Handle text nodes with formatting
  if (node.getType() === 'text') {
    const text = node.getTextContent();
    const format = node.getFormat();

    // Apply markdown formatting based on format flags
    let formattedText = text;
    if (format & 1) formattedText = `**${formattedText}**`; // Bold
    if (format & 2) formattedText = `*${formattedText}*`; // Italic
    if (format & 8) formattedText = `~~${formattedText}~~`; // Strikethrough
    if (format & 32) formattedText = `\`${formattedText}\``; // Code

    return formattedText;
  }

  for (const transformer of transformers) {
    if ('export' in transformer && typeof transformer.export === 'function') {
      const result = transformer.export(
        node,
        (elementNode: any) =>
          elementNode
            .getChildren()
            .map((child: any) => exportNodeToMarkdown(child, transformers))
            .join(''),
        (_textNode: any, textContent: string) => textContent
      );
      if (result != null) {
        return result;
      }
    }
  }
  // Fallback to text content if no transformer matches
  if (typeof node.getTextContent === 'function') {
    return node.getTextContent();
  }
  return '';
}

const TABLE_TRANSFORMER: ElementTransformer = {
  export: (node) => {
    // if (!node.isAttached()) {
    //   return null;
    // }

    if (node.getType() === 'paragraph') {
      const paragraphNode = node as ParagraphNode;

      const childrenMarkdown = paragraphNode
        .getChildren()
        .map((child) =>
          exportNodeToMarkdown(child, [
            // LIST_TRANSFORMER,
            ...LEXICAL_TRANSFORMERS,
            ...ELEMENT_TRANSFORMERS,
            ...TEXT_FORMAT_TRANSFORMERS,
            ...TEXT_MATCH_TRANSFORMERS,
          ])
        )
        .join('');

      return childrenMarkdown;
    }

    if (node.getType() === 'table') {
      const tableNode = node as TableNode;
      const rows = tableNode.getChildren() as TableRowNode[];
      if (rows.length === 0) return '';

      // Header row
      const headerCells = rows[0].getChildren() as TableCellNode[];
      const header =
        '|' +
        headerCells
          .map((cell) =>
            cell
              .getChildren()
              .map((child) =>
                exportNodeToMarkdown(child, [
                  // LIST_TRANSFORMER,
                  ...LEXICAL_TRANSFORMERS,
                  ...ELEMENT_TRANSFORMERS,
                  ...TEXT_FORMAT_TRANSFORMERS,
                  ...TEXT_MATCH_TRANSFORMERS,
                ])
              )
              .join('\n')
              .replace(/\n/g, '<br>')
              .trim()
          )
          .join(' | ') +
        '|';

      // Separator row
      const separator = '|' + headerCells.map(() => '---').join(' | ') + '|';

      // Body rows
      const bodyRows = rows.slice(1).map((row) => {
        const cells = row.getChildren() as TableCellNode[];
        return (
          '|' +
          cells
            .map((cell) =>
              cell
                .getChildren()
                .map((child) =>
                  exportNodeToMarkdown(child, [
                    // LIST_TRANSFORMER,
                    ...LEXICAL_TRANSFORMERS,
                    ...ELEMENT_TRANSFORMERS,
                    ...TEXT_FORMAT_TRANSFORMERS,
                    ...TEXT_MATCH_TRANSFORMERS,
                  ])
                )
                .join('\n')
                .replace(/\n/g, '<br>')
                .trim()
            )
            .join(' | ') +
          '|'
        );
      });

      return [header, separator, ...bodyRows].join('\n');
    }
    return null;
  },
  regExp: /^\|(.+)\|$/,
  replace: (parentNode, _children, match) => {
    const tableNode = new TableNode();
    const rowContent = match[1].split('|').map((cell) => cell.trim());
    const row = (tableNode as any).createRow();
    rowContent.forEach((content) => {
      const cell = row.createCell();
      // Parse cell content as markdown and append nodes
      const nodes = $convertFromMarkdownString(content.replace(/<br>/g, '\n'), [
        // LIST_TRANSFORMER,
        ...LEXICAL_TRANSFORMERS,
        ...ELEMENT_TRANSFORMERS,
        ...TEXT_FORMAT_TRANSFORMERS,
        ...TEXT_MATCH_TRANSFORMERS,
      ]);
      if (Array.isArray(nodes)) {
        nodes.forEach((n) => cell.append(n));
      }
    });
    parentNode.replace(tableNode);
    return true;
  },
  type: 'element',
  dependencies: [TableNode, TableRowNode, TableCellNode],
};

const FILTERED_ELEMENT_TRANSFORMERS = ELEMENT_TRANSFORMERS.filter(
  (transformer) => transformer.regExp?.toString() !== /^(#{1,6})\s/.toString()
);

/** Planning / template (`enhancedHeadings`): markdown `#` → EnhancedHeadingNode. Must append Lexical `children` (import pre-strips `match[0]`). */
const ENHANCED_HEADING_TRANSFORMERS: ElementTransformer[] = [
  {
    dependencies: [],
    export: (node) => {
      if (node.getType() === 'heading') {
        const headingNode = node as any;
        const level = headingNode.getTag();
        const text = headingNode.getTextContent();
        return '#'.repeat(parseInt(level.replace('h', ''), 10)) + ' ' + text;
      }
      return null;
    },
    regExp: /^(#{1,6})\s/,
    replace: (parentNode, children: LexicalNode[], match, isImport) => {
      const level = match[1].length;
      const headingNode = $createEnhancedHeadingNode(
        `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
      );
      headingNode.append(...children);
      parentNode.replace(headingNode);
      if (!isImport) {
        headingNode.select(0, 0);
      }
      return true;
    },
    type: 'element',
  },
];

/** Default rich text: Lexical stock `HeadingNode` (workstreams, most surfaces). */
export const TRANSFORMERS = [
  ...ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
  TABLE_TRANSFORMER,
];

/** Planning app + template mode with enhanced headings. */
export const TRANSFORMERS_WITH_ENHANCED_HEADINGS = [
  ...ENHANCED_HEADING_TRANSFORMERS,
  ...FILTERED_ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
  TABLE_TRANSFORMER,
];

// Export transformers without heading support (for document mode)
export const TRANSFORMERS_WITHOUT_HEADINGS = [
  ...FILTERED_ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
  TABLE_TRANSFORMER,
];
